import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, TrendingUp, ListTodo, PoundSterling, Megaphone, AlertTriangle, UserX, Target } from "lucide-react";

export default function CrmDashboard() {
  const [k, setK] = useState(null);
  const [scoring, setScoring] = useState(null);
  useEffect(() => {
    api.get("/crm/dashboard").then(r => setK(r.data));
    api.get("/crm/scoring/stats").then(r => setScoring(r.data)).catch(() => {});
  }, []);

  if (!k) return <div className="text-sm text-muted-foreground py-8">Loading…</div>;

  const cards = [
    { label: "Total Leads", value: k.leads_total, icon: Users, tid: "crm-kpi-leads" },
    { label: "Total Deals", value: k.deals_total, icon: TrendingUp, tid: "crm-kpi-deals" },
    { label: "Won Value (£)", value: k.won_value_gbp.toLocaleString(), icon: PoundSterling, tid: "crm-kpi-value" },
    { label: "Open Tasks", value: k.open_tasks, icon: ListTodo, tid: "crm-kpi-tasks" },
    { label: "Campaigns", value: k.campaigns_total, icon: Megaphone, tid: "crm-kpi-campaigns" },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {cards.map(c => (
          <div key={c.label} data-testid={c.tid} className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider">
              <c.icon className="w-3.5 h-3.5" /> {c.label}
            </div>
            <div className="mt-2 font-display text-3xl font-light">{c.value}</div>
          </div>
        ))}
      </div>

      {scoring && (
        <div className="rounded-xl border border-border bg-card p-5" data-testid="crm-scoring-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2"><Target className="w-4 h-4" /> Lead scoring & SLA</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {["SQL", "MQL", "unqualified", "disqualified"].map(q => (
              <div key={q} className="rounded-lg bg-muted/50 p-3" data-testid={`crm-scoring-${q}`}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{q}</div>
                <div className="mt-1 font-display text-2xl font-light">{scoring.by_qualification?.[q] || 0}</div>
              </div>
            ))}
            <div className="rounded-lg bg-rose-500/10 border border-rose-500/20 p-3" data-testid="crm-scoring-breached">
              <div className="text-[10px] uppercase tracking-wider text-rose-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> SLA breached</div>
              <div className="mt-1 font-display text-2xl font-light text-rose-600">{scoring.sla_breached || 0}</div>
            </div>
            <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-3" data-testid="crm-scoring-unassigned">
              <div className="text-[10px] uppercase tracking-wider text-amber-600 flex items-center gap-1"><UserX className="w-3 h-3" /> Unassigned</div>
              <div className="mt-1 font-display text-2xl font-light text-amber-700">{scoring.unassigned || 0}</div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="font-semibold mb-3">Lead pipeline</h3>
          <div className="space-y-2">
            {k.lead_pipeline.map(p => (
              <div key={p.stage} className="flex items-center gap-3">
                <div className="w-24 text-xs capitalize text-muted-foreground">{p.stage}</div>
                <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                  <div className="bg-primary h-full" style={{ width: `${Math.min(100, (p.count / (k.leads_total || 1)) * 100)}%` }} />
                </div>
                <div className="text-sm font-medium w-8 text-right">{p.count}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="font-semibold mb-3">Deal pipeline by value (£)</h3>
          <div className="space-y-2">
            {k.deal_pipeline.map(p => (
              <div key={p.stage} className="flex items-center gap-3">
                <div className="w-28 text-xs capitalize text-muted-foreground">{p.stage}</div>
                <div className="flex-1 flex items-center gap-2">
                  <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                    <div className="bg-secondary h-full" style={{ width: `${Math.min(100, (p.value_gbp / (k.won_value_gbp + 1)) * 100)}%` }} />
                  </div>
                  <div className="text-xs text-muted-foreground w-16 text-right">£{p.value_gbp.toLocaleString()}</div>
                </div>
                <div className="text-xs w-6 text-right">{p.count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
